import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronLeft, ChevronRight, Leaf, Heart, Hammer, 
  Recycle, Award, Sun, ArrowRight 
} from 'lucide-react';
import './Home.css'; // Make sure to import the CSS file
import HeroSlider from './Slider/Slider';
import ValuesSections from './ValuesSection/ValuesSection';
import HowWeWork from './HowWeWork/HowWeWork';
import crafting from './Crafting.jpg';
import belive from './belive.png';
import SEO from '../SEO';
const API_URL = 'https://api.sohanihandicrafts.com:5010';



// --- COMPONENTS ---


// 2. WELCOME SECTION
const WelcomeSection = () => {
  return (
    <section className="welcome-section">
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        className="bg-decoration"
      >
        <svg viewBox="0 0 100 100">
            <path d="M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90C27.9 90 10 72.1 10 50S27.9 10 50 10s40 17.9 40 40-17.9 40-40 40z"/>
            <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="1" fill="none" />
            <circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </motion.div>

      <div className="container welcome-container">
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="welcome-text"
        >
          <span className="small-heading">Welcome to WoodCraft</span>
          <h2>Crafting Nature into <br /> <span>Timeless Art</span></h2>
          <p>
            We believe that every piece of wood holds a spirit waiting to be revealed. 
            Our master artisans combine centuries-old techniques with modern aesthetics 
            to create decor that feels alive.
          </p>
          <div className="btn-group">
            <button className="btn-primary">Know More</button>
            <button className="btn-outline">Contact Us</button>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="welcome-image-wrapper"
        >
          <div className="welcome-img-shape">
            <img src={crafting} alt="Carpenter" />
          </div>
          <div className="welcome-img-border"></div>
        </motion.div>
      </div>
    </section>
  );
};

// 3. COLLECTIONS SLIDER
const CollectionSection = () => {
  const [categories, setCategories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();

  // 1. Fetch Categories
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/category/categories?limit=20`);
        setCategories(res.data.categories);
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCats();
  }, []);

  // 2. Responsive Logic: Items per view
  const getItemsPerView = () => {
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 4; // Desktop
  };

  const itemsPerView = getItemsPerView();
  const maxIndex = Math.max(0, categories.length - itemsPerView);

  // 3. Slider Controls
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  // 4. Auto Slide Logic
  useEffect(() => {
    if (isPaused || categories.length === 0) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, categories]);

  return (
    <section className="cs-section">
      <div className="cs-container">
        
        {/* Header with Navigation Arrows */}
        <div className="cs-header">
          <div className="cs-header-text">
            <span className="cs-tag">Our Masterpieces</span>
            <h2>Collections that <span className="italic">Spark Creativity</span></h2>
          </div>
          
          <div className="cs-nav-btns">
            <button onClick={prevSlide} className="cs-arrow-btn"><ChevronLeft /></button>
            <button onClick={nextSlide} className="cs-arrow-btn"><ChevronRight /></button>
          </div>
        </div>

        {/* The Slider Window */}
        <div 
          className="cs-slider-window"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <motion.div 
            className="cs-slider-track"
            animate={{ x: `-${currentIndex * (100 / itemsPerView)}%` }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
          >
            {categories.map((cat) => (
              <div 
                className="cs-slide-item" 
                key={cat._id}
                style={{ flex: `0 0 ${100 / itemsPerView}%` }}
              >
                <div 
                  className="cs-product-card" 
                  onClick={() => navigate(`/product/${cat._id}`)}
                >
                  <div className="cs-img-box">
                    <img src={`${API_URL}/${cat.image}`} alt={cat.name} />
                    <div className="cs-overlay">
                        <button>Explore Piece</button>
                    </div>
                  </div>
                  <div className="cs-info">
                    <h3>{cat.name}</h3>
                    <div className="cs-price-row">
                      <span>Artisan Collection</span>
                      <ArrowRight size={18} className="cs-arrow-icon" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Progress Indicators (Dots) */}
        <div className="cs-dots">
            {categories.slice(0, maxIndex + 1).map((_, i) => (
                <div 
                    key={i} 
                    className={`cs-dot ${currentIndex === i ? 'active' : ''}`}
                    onClick={() => setCurrentIndex(i)}
                />
            ))}
        </div>
      </div>
    </section>
  );
};

// 4. VALUES SECTION
const ValuesSection = () => {
  return (
    <section className="values-section">
      <div className="container">
        <h2 className="center-heading">What We Believe For</h2>
        
        <div className="values-grid">
          {/* Left */}
          <div className="values-col left">
            <ValueItem icon={<Leaf size={28} />} title="Eco-Friendly" desc="Certified sustainable forests." align="right" />
            <ValueItem icon={<Hammer size={28} />} title="Handcrafted" desc="Pure craftsmanship, no machines." align="right" />
            <ValueItem icon={<Sun size={28} />} title="Natural Finishes" desc="Organic oils and waxes." align="right" />
          </div>

          {/* Center */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            className="values-center-img"
          >
             <img src={belive} alt="Core Values" />
          </motion.div>

          {/* Right */}
          <div className="values-col right">
             <ValueItem icon={<Heart size={28} />} title="Made with Love" desc="Passion is our main ingredient." align="left" />
             <ValueItem icon={<Award size={28} />} title="Quality First" desc="Built to last generations." align="left" />
             <ValueItem icon={<Recycle size={28} />} title="Zero Waste" desc="Nothing goes to waste." align="left" />
          </div>
        </div>
      </div>
    </section>
  );
};

const ValueItem = ({ icon, title, desc, align }) => (
  <motion.div 
    initial={{ opacity: 0, x: align === 'right' ? 20 : -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className={`value-item ${align}`}
  >
    <div className="icon-box">{icon}</div>
    <div className="text-box">
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  </motion.div>
);


const Home = () => {
  return (
    <>
   <SEO 
  title="Sohani Handicrafts | Premium Jodhpur Wooden Furniture & Artisan Decor"
  description="Sohani Handicrafts is a leading manufacturer and exporter of handcrafted wooden furniture, industrial iron items, and marble handicrafts from Jodhpur, India. Global shipping available."
  keywords="jodhpur handicrafts, wooden furniture manufacturer, indian furniture exporter, marble handicrafts, artisan decor"
  url="/"
/>
    <div className="app-wrapper">
     <HeroSlider/>
      <WelcomeSection />
      <CollectionSection />
      <ValuesSection />
      <ValuesSections/>
      <HowWeWork/>
    </div>
    </>
  );
};

export default Home;